export interface BlogPost {
  id: number
  slug: string
  title: string
  date: string
  excerpt: string
  readTime: string
  fullContent: string
}

export interface MilestoneMarker {
  id: number
  type: 'milestone'
  milestone: string
  subtitle?: string
}

export type TimelineItem = BlogPost | MilestoneMarker

export const timelineItems: TimelineItem[] = [
  {
    id: 1,
    slug: "the-search-begins",
    title: "Could AI Run a Company?",
    date: "August 25, 2025",
    excerpt: "It's a question we've been thinking about at TRPC: Could AI ever truly run a company?",
    readTime: "3 min read",
    fullContent: `
      <p>It's a question we've been thinking about at TRPC:</p>
      <p><strong>Could AI ever truly run a company?</strong></p>
      
      <p>Not just assist with emails or automate a few tasks but actually operate as if it were the CEO? Making decisions, managing clients, driving strategy.</p>
      
      <p>We're nowhere near that today. You can't just drop AI into the driver's seat and expect it to steer. Running a business isn't a linear checklist. It's messy. It's context-heavy. It requires judgment, timing, nuance, and sometimes intuition.</p>
      
      <p>For AI to run a company, a few things would need to happen:</p>
      
      <p><strong>Structured training:</strong> It would need to learn the ropes like a junior hire.</p>
      
      <p><strong>Continuous feedback:</strong> It would need oversight and correction, especially in the early days.</p>
      
      <p><strong>Expanded capabilities:</strong> From handling calls to drafting proposals to eventually understanding the business as a whole.</p>
      
      <p><strong>Trust:</strong> Maybe the hardest part. People inside and outside the company would need to actually rely on it.</p>
      
      <p>That's a mountain of a challenge. But it's becoming harder to ignore the possibility.</p>
      
      <p>AI is already handling real business functions today; sales, support, operations. The question isn't whether it will be involved. It's how far we're willing to push.</p>
      
      <p>At TRPC, we've been asking ourselves: what does it look like to start climbing that mountain?</p>
    `
  },
  {
    id: 2,
    slug: "first-interviews",
    title: "We're Hiring Our First AI Intern", 
    date: "September 8, 2025",
    excerpt: "At The Rapid Prototyping Company, we're at capacity. We decided to do something different.",
    readTime: "5 min read",
    fullContent: `
      <p>At The Rapid Prototyping Company, we're at capacity. Between discovery calls, proposals, and project builds, we're spread thin.</p>
      
      <p>We decided to do something different.</p>
      
      <p><strong>We're hiring an AI intern.</strong></p>
      
      <p>Here's the plan:</p>
      
      <p>Each co-founder (Matt and I) built a candidate.</p>
      
      <p>We also created a neutral third option using AI.</p>
      
      <p>Over the next two weeks, you'll get to meet them, test them, and help us decide who to hire.</p>
      
      <p>From there, the intern starts working for us: taking discovery calls, doing first drafts, learning new skills. We'll share the good, the bad, and the awkward moments along the way.</p>
      
      <p>This isn't about replacing people. It's about solving a problem we face right now: we need help, and we believe AI can step up.</p>
      
      <p>Step one: Meet the candidates. Who should we hire?</p>
    `
  },
  {
    id: 3,
    type: 'milestone' as const,
    milestone: "Interview Process Begins",
    subtitle: "Our candidates are put in front of real people. You help us choose the right one."
  },
  {
    id: 4,
    type: 'milestone' as const,
    milestone: "Intern is Hired",
    subtitle: "We review the results and make a selection."
  },
  {
    id: 5,
    type: 'milestone' as const,
    milestone: "First Week",
    subtitle: "Our AI Intern is live and we are a week in."
  },
  {
    id: 6,
    type: 'milestone' as const,
    milestone: "First Performance Evaluation",
    subtitle: "We evaluate our AI intern to see if its delivering"
  },
  {
    id: 7,
    type: 'milestone' as const,
    milestone: "New Responsibilities",
    subtitle: "We give the intern new skills and some more responsibilities."
  },
  {
    id: 8,
    type: 'milestone' as const,
    milestone: "A Bigger Role",
    subtitle: "We let our AI Intern take the reins on a bigger task or project."
  }
]

// Legacy export for compatibility
export const blogPosts = timelineItems.filter((item): item is BlogPost => !('type' in item))